import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import SecureVideoPlayer from '@/components/SecureVideoPlayer';
import { PlayCircle, CheckCircle, Lock, Clock, ArrowLeft, Users, ChevronRight, Award, Shield, Sparkles, Download } from 'lucide-react';
import { useGeoCurrency } from '@/hooks/useGeoCurrency';

function PlayerPlaceholder({ label = 'Video coming soon' }) {
  return (
    <div className="aspect-video bg-gradient-to-br from-heading/90 to-warm-black flex items-center justify-center rounded-xl">
      <div className="text-center">
        <PlayCircle size={48} className="text-sacred-gold/40 mx-auto mb-3" />
        <p className="text-cream/50 font-body text-sm">{label}</p>
      </div>
    </div>
  );
}

function YouTubeEmbed({ url }) {
  let videoId = '';
  if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1]?.split('?')[0];
  else if (url.includes('v=')) videoId = url.split('v=')[1]?.split('&')[0];
  else if (url.includes('embed/')) videoId = url.split('embed/')[1]?.split('?')[0];
  if (!videoId) return <PlayerPlaceholder label="Invalid YouTube URL" />;
  return (
    <div className="aspect-video rounded-xl overflow-hidden">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?rel=0`}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Course Video"
      />
    </div>
  );
}

function EpisodeVideo({ episode, onEnded }) {
  if (!episode) return <PlayerPlaceholder />;
  if (episode.video_id) {
    return <SecureVideoPlayer videoId={episode.video_id} chapters={episode.chapters || []} onEnded={onEnded} />;
  }
  if (episode.video_url) return <YouTubeEmbed url={episode.video_url} />;
  return <PlayerPlaceholder />;
}

const levelBadge = {
  beginner: { text: 'Seeker', color: 'bg-amber-100/10 text-amber-200 border-amber-200/20' },
  intermediate: { text: 'Thinker', color: 'bg-blue-100/10 text-blue-200 border-blue-200/20' },
  advanced: { text: 'Builder', color: 'bg-purple-100/10 text-purple-200 border-purple-200/20' },
  master: { text: 'Sovereign', color: 'bg-yellow-100/10 text-yellow-200 border-yellow-200/30' },
};

export default function CourseDetail() {
  const { slug } = useParams();
  const { t, localizedField } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency } = useGeoCurrency();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: c } = await api.get(`/courses/${slug}`);
        setCourse(c);
        if (c.episodes?.length) setActiveEpisode(c.episodes[0]);
        if (user) {
          try {
            const { data: e } = await api.get(`/courses/${slug}/enrollment`);
            if (e) {
              setEnrollment(e);
              setShowIntro(false);
              if (e.last_episode_id && c.episodes) {
                const lastEp = c.episodes.find(ep => ep.id === e.last_episode_id);
                if (lastEp) setActiveEpisode(lastEp);
              }
            }
          } catch {}
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [slug, user]);

  const handleEnroll = async () => {
    if (!user) { window.location.href = '/auth'; return; }
    setEnrolling(true);
    try {
      const { data } = await api.post(`/courses/${slug}/enroll`);
      setEnrollment(data);
      setShowIntro(false);
    } catch {}
    setEnrolling(false);
  };

  const handleEpisodeSelect = async (ep) => {
    if (!enrollment && !ep.is_preview) return;
    setActiveEpisode(ep);
    setShowIntro(false);
    if (enrollment) {
      try { await api.put(`/courses/${slug}/progress`, { last_episode_id: ep.id }); } catch {}
    }
  };

  const handleEpisodeComplete = async () => {
    if (!enrollment || !activeEpisode) return;
    try {
      const { data } = await api.put(`/courses/${slug}/progress`, { completed_episode_id: activeEpisode.id });
      setEnrollment(data);
    } catch {}
  };

  const downloadCertificate = async () => {
    try {
      const response = await api.get(`/certificates/${slug}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TariqWithin-Certificate-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Certificate not available yet');
    }
  };

  const isEpisodeCompleted = (epId) => enrollment?.completed_episodes?.includes(epId);
  const completedCount = enrollment?.completed_episodes?.length || 0;
  const totalEpisodes = course?.episodes?.length || 0;
  const progressPercent = totalEpisodes > 0 ? Math.round((completedCount / totalEpisodes) * 100) : 0;
  const isCompleted = !!enrollment?.completed_at;

  if (loading) return <div className="min-h-screen flex items-center justify-center text-text-muted font-body" data-testid="course-loading">{t('common.loading')}</div>;
  if (!course) return <div className="min-h-screen flex items-center justify-center text-text-muted font-body">{t('common.notFound')}</div>;

  const hasIntro = !!(course.intro_video_id || course.intro_video_url);
  const lvl = levelBadge[course.level || 'beginner'] || levelBadge.beginner;

  const renderIntro = () => {
    if (course.intro_video_id) return <SecureVideoPlayer videoId={course.intro_video_id} />;
    if (course.intro_video_url) return <YouTubeEmbed url={course.intro_video_url} />;
    return <PlayerPlaceholder label="Intro video coming soon" />;
  };

  return (
    <div data-testid="course-detail-page" className="min-h-screen pb-20">
      <div className="px-4 pt-6 pb-4 max-w-7xl mx-auto">
        <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-heading font-body transition-colors" data-testid="back-to-courses">
          <ArrowLeft size={16} /> {t('courses.title')}
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <ScrollReveal>
              {/* Intro toggle row — only visible if course has an intro */}
              {hasIntro && (
                <div className="flex items-center gap-2 mb-3 bg-cream/5 border border-cream/10 rounded-full p-1 w-fit" data-testid="intro-toggle">
                  <button
                    onClick={() => setShowIntro(true)}
                    className={`px-3 py-1 rounded-full text-xs font-body flex items-center gap-1 transition-colors ${showIntro ? 'bg-sacred-gold text-black' : 'text-text-muted hover:text-heading'}`}
                    data-testid="show-intro"
                  >
                    <Sparkles size={12} /> Course Trailer (Free)
                  </button>
                  <button
                    onClick={() => setShowIntro(false)}
                    className={`px-3 py-1 rounded-full text-xs font-body flex items-center gap-1 transition-colors ${!showIntro ? 'bg-sacred-gold text-black' : 'text-text-muted hover:text-heading'}`}
                    data-testid="show-episode"
                  >
                    <PlayCircle size={12} /> Episode
                  </button>
                </div>
              )}

              {showIntro && hasIntro ? renderIntro() : (
                <EpisodeVideo episode={activeEpisode} onEnded={handleEpisodeComplete} />
              )}
            </ScrollReveal>

            <div className="mt-6">
              <div className="flex items-start flex-wrap gap-2 mb-2">
                <h1 className="text-2xl sm:text-3xl font-display text-heading" data-testid="course-title">{localizedField(course.title)}</h1>
                <span className={`px-2 py-0.5 text-[10px] font-body uppercase tracking-wider rounded-full border ${lvl.color}`} data-testid="course-level-badge">
                  <Award size={10} className="inline mr-1" /> {lvl.text}
                </span>
              </div>
              <p className="text-text-muted font-body text-sm mb-4">by {course.instructor || 'Tariq AbuFiad'}</p>

              {!showIntro && activeEpisode && (
                <div className="glass-card rounded-xl p-4 mb-4" data-testid="active-episode-info">
                  <h3 className="font-display text-heading text-lg">{localizedField(activeEpisode.title)}</h3>
                  <p className="text-text-muted font-body text-sm mt-1">{localizedField(activeEpisode.description)}</p>
                  {(activeEpisode.chapters || []).length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-text-muted font-body mb-1">Chapters:</p>
                      <div className="flex flex-wrap gap-1">
                        {activeEpisode.chapters.map((c, i) => (
                          <span key={i} className="px-2 py-0.5 text-[10px] font-body rounded-full bg-sacred-gold/10 text-sacred-gold">
                            {Math.floor(c.time / 60)}:{String(c.time % 60).padStart(2, '0')} · {c.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {enrollment && (
                    <button
                      onClick={handleEpisodeComplete}
                      disabled={isEpisodeCompleted(activeEpisode.id)}
                      className={`mt-3 px-4 py-2 rounded-full font-body text-sm transition-all ${isEpisodeCompleted(activeEpisode.id) ? 'bg-accent-success/10 text-accent-success cursor-default' : 'gold-button'}`}
                      data-testid="mark-complete-btn"
                    >
                      {isEpisodeCompleted(activeEpisode.id) ? 'Completed' : 'Mark as Complete'}
                    </button>
                  )}
                </div>
              )}

              <ScrollReveal delay={0.1}>
                <div className="mt-6">
                  <h2 className="font-display text-heading text-lg mb-3">About this course</h2>
                  <p className="text-text-muted font-body text-sm leading-relaxed">{localizedField(course.description)}</p>
                </div>
              </ScrollReveal>
            </div>
          </div>

          <div className="lg:col-span-1">
            {!enrollment && (
              <ScrollReveal>
                <div className="glass-card rounded-xl p-5 mb-6 sticky top-24" data-testid="enroll-card">
                  <div className="text-center mb-4">
                    {course.is_free ? (
                      <span className="text-2xl font-display text-accent-success">Free</span>
                    ) : (
                      <span className="text-2xl font-display text-heading">{currency === 'ILS' && course.price_ils ? `\u20AA${course.price_ils}` : `$${course.price_usd}`}</span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      // Paid items go straight to checkout (guest checkout supported);
                      // only free self-enrollment needs an account
                      if (!course.is_free) {
                        navigate(`/checkout?type=course&id=${course.id}&currency=${currency}`);
                        return;
                      }
                      if (!user) { navigate('/auth', { state: { from: window.location.pathname } }); return; }
                      handleEnroll();
                    }}
                    disabled={enrolling}
                    className="w-full gold-button py-3 rounded-full font-body text-sm font-semibold"
                    data-testid="enroll-btn"
                  >
                    {enrolling ? 'Enrolling...' : course.is_free ? t('courses.enroll') : `Buy — ${currency === 'ILS' && course.price_ils ? `\u20AA${course.price_ils}` : `$${course.price_usd}`}`}
                  </button>
                  <div className="mt-4 text-xs text-text-muted font-body space-y-2">
                    <div className="flex items-center gap-2"><PlayCircle size={14} /> {totalEpisodes} episodes</div>
                    <div className="flex items-center gap-2"><Clock size={14} /> {course.episodes?.reduce((s, e) => s + (e.duration_minutes || 0), 0)} min total</div>
                    <div className="flex items-center gap-2"><Users size={14} /> {course.enrollment_count || 0} enrolled</div>
                    <div className="flex items-center gap-2"><Shield size={14} className="text-sacred-gold" /> Encrypted stream &middot; anti-piracy</div>
                    <div className="flex items-center gap-2"><Award size={14} className="text-sacred-gold" /> {lvl.text} certificate on completion</div>
                  </div>
                </div>
              </ScrollReveal>
            )}

            {enrollment && (
              <ScrollReveal>
                <div className="glass-card rounded-xl p-5 mb-6" data-testid="progress-card">
                  <h3 className="font-display text-heading text-sm mb-3">Your Progress</h3>
                  <div className="w-full bg-cream/20 rounded-full h-2 mb-2">
                    <div className="bg-sacred-gold h-2 rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }} />
                  </div>
                  <p className="text-xs text-text-muted font-body">{completedCount}/{totalEpisodes} episodes ({progressPercent}%)</p>
                  {isCompleted && (
                    <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-sacred-gold/20 to-transparent border border-sacred-gold/30" data-testid="certificate-block">
                      <div className="flex items-center gap-2 text-sacred-gold font-display text-sm mb-2">
                        <Award size={16} /> Course Completed
                      </div>
                      <p className="text-xs text-text-muted font-body mb-3">Your {lvl.text} certificate is ready.</p>
                      <button
                        onClick={downloadCertificate}
                        className="w-full gold-button py-2 rounded-full font-body text-xs font-semibold flex items-center justify-center gap-2"
                        data-testid="download-certificate-btn"
                      >
                        <Download size={12} /> Download Certificate (PDF)
                      </button>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            <ScrollReveal delay={0.1}>
              <div className="glass-card rounded-xl p-5" data-testid="episode-list">
                <h3 className="font-display text-heading text-sm mb-4">Episodes</h3>
                <div className="space-y-1">
                  {(course.episodes || []).sort((a, b) => a.sort_order - b.sort_order).map((ep, i) => {
                    const isLocked = !enrollment && !ep.is_preview;
                    const isActive = !showIntro && activeEpisode?.id === ep.id;
                    const isComp = isEpisodeCompleted(ep.id);
                    return (
                      <button
                        key={ep.id}
                        onClick={() => handleEpisodeSelect(ep)}
                        disabled={isLocked}
                        className={`w-full text-left rtl:text-right flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-body ${
                          isActive ? 'bg-sacred-gold/10 text-heading' : isLocked ? 'text-text-muted/50 cursor-not-allowed' : 'text-text-muted hover:bg-cream/30 hover:text-heading'
                        }`}
                        data-testid={`episode-${ep.id}`}
                      >
                        <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">
                          {isComp ? <CheckCircle size={16} className="text-accent-success" /> :
                           isLocked ? <Lock size={14} className="text-text-muted/40" /> :
                           <span className="text-dust-gray">{i + 1}</span>}
                        </span>
                        <span className="flex-1 min-w-0 truncate">{localizedField(ep.title)}</span>
                        {ep.is_preview && !isComp && <span className="text-[9px] px-1.5 py-0.5 bg-sacred-gold/20 text-sacred-gold rounded-full shrink-0">FREE</span>}
                        <span className="text-xs text-dust-gray shrink-0">{ep.duration_minutes}m</span>
                        {isActive && <ChevronRight size={14} className="text-sacred-gold shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </div>
  );
}
